<template>
  <section class="section card" :class="cardClass">
    <div
      class="section-header"
      v-if="title || $slots.headerActions || showUpdateButton"
    >
      <h3 v-if="title">
        <div>{{ title }}</div>
      </h3>
      <div class="header-actions">
        <slot name="headerActions" v-if="$slots.headerActions"></slot>
        <button
          v-if="showUpdateButton && !$slots.headerActions && !isLoading"
          class="link"
          :class="{ disabled: isProductDisabled }"
          :disabled="isProductDisabled"
          @click="$emit('requestUpdate')"
        >
          <Icons name="EditIcon" size="sm" :color="isProductDisabled ? '#B4B4B4' : '#FF44EE'" />
          {{ updateButtonText }}
        </button>
      </div>
    </div>
 
    <div class="card-content" :class="contentClass">
      <slot></slot>
      <div v-if="showApplyToVariants" class="input-with-info-variant">
        <div v-if="!isOptionsVisible" class="left-actions">
          <slot  name="extraActionButton"></slot>
        </div>
        <div class="right-section">
          <div v-if="showOtherOptions" class="show-options">
            <div v-if="isOptionsVisible" class="options-divider"></div>
            <button class="show-options-button" @click="handleChangeShowOptions">
              <Icons v-if="!isOptionsVisible" name="Eye" color="#FF44EE" />
              <Icons v-else name="EyeClose" color="#FF44EE" />
              <p>Ürünün Diğer Seçeneklerini Gör</p>
            </button>
          </div>
          <div v-if="applyVariantsAllCheckbox" :class="['apply-variants', { disabled: isSaved || isSaving || !isTemplateSelected || isProductDisabled}]">
            <input
              type="checkbox"
              :id="applyCheckboxId + '-' + cardKey"
              v-model="localApplyToVariants"
              @change="$emit('update:applyToVariants', $event.target.checked)"
              :disabled="isSaved || isSaving || !isTemplateSelected || isProductDisabled"
            />
            <label :for="applyCheckboxId + '-' + cardKey">{{ applyVariantsText }}</label>
          </div>
          <div class="actions-row">
            <Button
              primary
              md
              @click="$emit('saveChanges')"
              :buttonStyle="{ width: saveButtonWidth }"
              :disabled="isSaved || isSaving || !isTemplateSelected || isProductDisabled"
            >
              <span style="display: flex; align-items: center; gap: 8px; justify-content: center;">
                <span v-if="isSaving" class="spinner"></span>
                <Icons v-else-if="showSuccessIcon" name="Checked" color="#B4B4B4" size="md" />
                {{ currentButtonText }}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
    <template v-if="isOptionsVisible">
      <slot name="options"></slot>
    </template>
  </section>
</template>
 
<script setup>
import { ref, watch, computed } from "vue";
import { useStore } from "vuex";
import { Icons, Button } from "@n11/so-ui-components";
 
const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  cardClass: {
    type: [String, Object],
    default: "",
  },
  contentClass: {
    type: String,
    default: "card-inner",
  },
  showUpdateButton: {
    type: Boolean,
    default: false,
  },
  updateButtonText: {
    type: String,
    default: "Güncelleme Talep Et / Hata Bildir",
  },
  showApplyToVariants: {
    type: Boolean,
    default: false,
  },
  showOtherOptions: {
    type: Boolean,
    default: false,
  },
  applyToVariants: {
    type: Boolean,
    default: false,
  },
  applyVariantsText: {
    type: String,
    default: "Değişiklikleri tüm varyantlara uygula.",
  },
  saveButtonText: {
    type: String,
    default: "Değişiklikleri Kaydet",
  },
  savedButtonText: {
    type: String,
    default: "Değişiklikler Kaydedildi",
  },
  saveButtonWidth: {
    type: String,
    default: "183px",
  },
  applyCheckboxId: {
    type: String,
    default: 'applyToVariants'
  },
  isSaved: {
    type: Boolean,
    default: false
  },
  showSuccessState: {
    type: Boolean,
    default: false
  },
  cardKey: {
    type: String,
    required: true
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  applyVariantsAllCheckbox: {
    type: Boolean,
    default: true,
  },
  isTemplateSelected: {
    type: Boolean,
    default: true,
  },
});
 
const emit = defineEmits([
  "updateButtonClick",
  "saveChanges",
  "update:applyToVariants",
]);
 
const store = useStore();
const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);
 
const localApplyToVariants = ref(props.applyToVariants);
const isOptionsVisible = ref(false)
 
const handleChangeShowOptions = async () => {
  isOptionsVisible.value = !isOptionsVisible.value
};
 
const currentButtonText = computed(() => {
  return props.showSuccessState ? props.savedButtonText : props.saveButtonText;
});
 
const showSuccessIcon = computed(() => {
  return props.showSuccessState;
});
 
watch(
  () => props.applyToVariants,
  (newValue) => {
    localApplyToVariants.value = newValue;
  }
);
</script>
 
<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
 
.section {
  width: 100%;
}
 
.card {
  border-radius: 8px;
  background: white;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.03);
  overflow: visible !important;
 
  &.product-disabled {
    filter: grayscale(100%) !important;
  }
}
 
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}
 
.section-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}
 
.header-actions .link {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.05px;
  color: $colorM50;
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: none;
}
 
.header-actions .link:hover:not(:disabled) {
  color: $colorM50;
}
 
.header-actions .link.disabled,
.header-actions .link:disabled {
  cursor: not-allowed;
  color: $colorN50;
}
 
.card-inner {
  padding: 16px 24px 24px 24px;
}
 
/* Apply to variants section styles */
.input-with-info-variant {
  gap: 16px;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
}
 
.left-actions {
  display: flex;
  align-items: center;
}
 
.right-section {
  display: flex;
  align-items: center;
  gap: 16px;
}
 
.apply-variants {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
}
 
.apply-variants input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: $colorN80;
}
 
.apply-variants label {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  cursor: pointer;
  user-select: none;
  color: #333;
}
 
.actions-row {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  align-items: center;
}
 
.d-flex {
  display: flex;
}
 
.show-options-button {
  display: flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
  flex: 0 0 auto;  
 
  p {
    color: $colorM50;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: -0.05px;
    margin: 0;
  }
}
 
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
 
.apply-variants.disabled {
  pointer-events: none;
}
 
.show-options {
  display: flex;
  align-items: center;
  justify-items: flex-end;
  gap: 16px;
  min-width: 0;
}

.options-divider {
  height: 0.5px;
  background: $colorN30;
  flex: 1 1 auto;
  min-width: 525px;
}
 
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
